import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, Text, HStack, VStack, SimpleGrid, Spinner,
  Stat, StatLabel, StatNumber, StatHelpText,
  Badge, Icon, Alert, AlertIcon, Button, Code, Divider,
} from '@chakra-ui/react';
import { FiRefreshCw, FiActivity, FiServer, FiTrendingUp, FiWifi } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function TrafficStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rps, setRps] = useState(null);
  const prevRequests = useRef(null);
  const prevTime = useRef(null);

  const fetchStatus = useCallback(() => {
    fetch(`${API_BASE}/traffic/nginx-status/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.error) {
          setError(data.error);
          setLoading(false);
          return;
        }
        // Calculate requests/sec from delta
        const now = Date.now();
        if (prevRequests.current !== null && prevTime.current !== null) {
          const dt = (now - prevTime.current) / 1000;
          if (dt > 0) {
            setRps(Math.round(((data.requests - prevRequests.current) / dt) * 100) / 100);
          }
        }
        prevRequests.current = data.requests;
        prevTime.current = now;

        setStatus(data);
        setError(null);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Traffic Manager Status</Heading>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchStatus}>Retry</Button>
      </HStack>
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <VStack align="start" spacing={1}>
          <Text fontWeight="bold">Failed to connect to traffic manager</Text>
          <Text fontSize="sm">{error}</Text>
        </VStack>
      </Alert>
    </Box>
  );

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Traffic Manager Status</Heading>
          <Text color="gray.500">
            Real-time nginx metrics &middot; Refreshes every 5 seconds
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchStatus}>Refresh</Button>
      </HStack>

      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="blue.400">
          <HStack mb={3}>
            <Icon as={FiWifi} boxSize={6} color="blue.500" />
            <Text fontWeight="bold" color="gray.600">Active Connections</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{status.active_connections}</StatNumber>
            <StatHelpText>Current open connections</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="green.400">
          <HStack mb={3}>
            <Icon as={FiTrendingUp} boxSize={6} color="green.500" />
            <Text fontWeight="bold" color="gray.600">Requests / sec</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{rps !== null ? rps : '—'}</StatNumber>
            <StatHelpText>Calculated from delta</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="purple.400">
          <HStack mb={3}>
            <Icon as={FiActivity} boxSize={6} color="purple.500" />
            <Text fontWeight="bold" color="gray.600">Total Requests</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl">{status.requests?.toLocaleString()}</StatNumber>
            <StatHelpText>Since last restart</StatHelpText>
          </Stat>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor="orange.400">
          <HStack mb={3}>
            <Icon as={FiServer} boxSize={6} color="orange.500" />
            <Text fontWeight="bold" color="gray.600">Dropped</Text>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl" color={status.dropped > 0 ? 'red.500' : undefined}>
              {status.dropped}
            </StatNumber>
            <StatHelpText>Accepted but not handled</StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* Connection Details */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="md" mb={4} color="gray.700">Connection Breakdown</Heading>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <Text color="gray.600">Reading</Text>
              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>{status.reading}</Badge>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Writing</Text>
              <Badge colorScheme="green" fontSize="md" px={3} py={1}>{status.writing}</Badge>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Waiting (keep-alive)</Text>
              <Badge colorScheme="purple" fontSize="md" px={3} py={1}>{status.waiting}</Badge>
            </HStack>
          </VStack>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="md" mb={4} color="gray.700">Server Lifetime</Heading>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <Text color="gray.600">Total Accepted</Text>
              <Text fontWeight="bold" fontSize="lg">{status.accepts?.toLocaleString()}</Text>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Total Handled</Text>
              <Text fontWeight="bold" fontSize="lg">{status.handled?.toLocaleString()}</Text>
            </HStack>
            <Divider />
            <HStack justify="space-between">
              <Text color="gray.600">Total Requests</Text>
              <Text fontWeight="bold" fontSize="lg">{status.requests?.toLocaleString()}</Text>
            </HStack>
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Raw output */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Heading size="md" mb={4} color="gray.700">Raw stub_status Output</Heading>
        <Code display="block" whiteSpace="pre" p={4} borderRadius="md" bg="gray.800" color="green.300" fontSize="sm">
          {status.raw}
        </Code>
      </Box>
    </Box>
  );
}

export default TrafficStatus;
